import type { boardEn } from './en.ts'

export const boardRu: typeof boardEn = {
  toolbar: {
    actionsAria: 'Действия с доской',
    groups: 'Группы:',
    players: 'Игроки:',
    filters: 'Фильтры',
    openFilters: 'Открыть фильтры игроков',
    openActiveFilters: (mode: string, count: number) => `Открыть фильтры игроков: режим «${mode}», активно ${count}`,
    viewOnly: 'Только просмотр — редактировать доску гильдии могут офицеры и владелец Discord-сервера.',
    openMap: 'Открыть тактическую карту рейда',
    map: 'Карта',
    raidAudit: 'Журнал рейда',
    raidAuditAria: 'Открыть журнал действий этого рейда',
    cloneRaidAria: 'Создать копию другого рейда из календаря',
    cloneRaid: 'Клонировать рейд',
    publish: 'Опубликовать',
    unpublish: 'Снять публикацию',
    publishAria: 'Опубликовать рейд',
    unpublishAria: 'Снять рейд с публикации',
    switchToDetail: 'Переключиться на подробный вид',
    switchToCompact: 'Переключиться на компактный вид',
    compact: 'Компактно',
    detail: 'Подробно',
    playerNameModeAria: 'Отображаемое имя игрока',
    guildNickname: 'Гильдия',
    playerNickname: 'Игрок',
    playerAccountName: 'Аккаунт',
    showGuildNickname: 'Показывать никнеймы Discord-сервера',
    showPlayerNickname: 'Показывать никнеймы игроков Discord',
    showPlayerAccountName: 'Показывать имена аккаунтов Discord',
    sortAria: 'Отсортировать все группы по ролям (клавиша: s)',
    sort: 'Сортировать',
    resetAria: 'Сбросить доску до начального состава',
    reset: 'Сбросить',
    sortedAnnouncement: 'Все группы отсортированы по ролям',
    resetAnnouncement: 'Доска сброшена до начального состава',
    resetConfirm: 'Сбросить доску до начального состава? Все изменения будут потеряны.',
  },
  player: {
    assignTagError: 'Не удалось назначить тег. Попробуйте ещё раз.',
    signedUp: 'Записался',
    // F-48: ARIA label is a neutral descriptive participle rather than the
    // conversational first-person past-tense form of the visible chip label
    // (`Записался / Не записался`). Screen-reader users hear it as a property
    // announcement of the player, not as the player speaking; the short-form
    // passive participle keeps the gender-neutral default that Russian
    // screen-reader style guides recommend. Mirrors the F-47 neutralisation
    // applied to the `signedIn*` / `notSigned*` ARIA strings above.
    signedUpAria: 'Записан на этот рейд',
    notSignedUp: 'Не записался',
    notSignedUpAria: 'Не записан на этот рейд',
    signInRaid: 'Записаться',
    signInRaidAria: 'Записаться на этот рейд',
    signOutRaid: 'Выйти',
    signOutRaidAria: 'Выйти из этого рейда',
    rsvpPending: 'Обновление…',
    rsvpError: 'Не удалось обновить запись на рейд. Попробуйте ещё раз.',
    rsvpSyncTimeout: 'Обновление рейда занимает больше времени, чем ожидалось. Данные появятся после синхронизации.',
    signedIn: 'Вошёл в планировщик',
    signedInAria: 'Вошёл в планировщик',
    notSigned: 'Не вошёл в планировщик',
    notSignedAria: 'Не вошёл в планировщик',
    inGame: 'В игре',
    addEmoji: 'Добавить эмодзи к игровому имени',
    filterByTag: (tag: string) => `Фильтровать по тегу «${tag}»`,
    toggleTagFilter: (tag: string) => `Переключить фильтр по тегу ${tag}`,
    additionalTagsDescription: (names: string) => `Дополнительные теги: ${names}`,
  },
  dropZone: {
    dropHere: 'Перетащите игроков сюда',
    noMatching: 'Нет подходящих игроков',
    noPlayers: 'Нет игроков',
  },
  group: {
    fallbackName: (index: number) => `Группа ${index}`,
    setName: (index: number) => `Задать название группы ${index}`,
    rename: (name: string | number) => `Переименовать группу ${name}`,
    unlimited: 'Без ограничения',
    capacityError: 'В группе больше или меньше игроков, чем положено по размеру',
    maxPlayers: (name: string) => `Максимум игроков в группе ${name}`,
    expand: (name: string) => `Развернуть ${name}`,
    collapse: (name: string) => `Свернуть ${name}`,
    selectPlayers: (name: string) => `Выбрать игроков для группы ${name}`,
  },
  role: {
    tank: 'ТАНК',
    // F-12: keep the cross-language MMO convention `DPS` rather than the
    // shorter Russian `ДД` so the badge's character width matches the
    // English counterpart (`DPS` = 3 glyphs, `ДД` = 2). The 2-glyph Russian
    // abbreviation collapses the centered label against the fixed-width
    // (`w-16`) colored halves in the badge; `DPS` is the universally
    // recognised damage-dealer term in the Russian MMO community (incl.
    // WWM) and avoids the unintended visual regression without touching
    // the chip's CSS dimensions.
    dps: 'DPS',
    hybrid: 'ГИБРИД',
    heal: 'ХИЛ',
    nameless: 'БЕЗ РОЛИ',
    unarmed: 'без оружия',
    badgeTitle: (role: string, left: string, right: string) => `Роль: ${role} — оружие: ${left} / ${right}`,
    statsTank: 'Танк',
    statsDps: 'ДД',
    statsHybrid: 'Гибрид',
    statsHealer: 'Хил',
  },
  room: {
    renameTitle: (name: string) => `Переименовать ${name}`,
    editableNameAria: (name: string) => `Название комнаты: ${name}. Нажмите, чтобы переименовать.`,
    nameAria: (name: string) => `Название комнаты: ${name}`,
    vertical: 'Вертикально',
    horizontal: 'Горизонтально',
    layoutDescription: (room: string, current: string, target: string) => `Расположение групп в комнате ${room}: ${current}. Нажмите, чтобы переключить на ${target}.`,
    moveUp: (name: string) => `Переместить комнату ${name} вверх`,
    moveDown: (name: string) => `Переместить комнату ${name} вниз`,
    moveUpTitle: 'Переместить комнату вверх',
    moveDownTitle: 'Переместить комнату вниз',
    removeAria: (name: string) => `Удалить комнату ${name}`,
    removeTitle: 'Удалить комнату',
    removeConfirm: (name: string) => `Удалить комнату «${name}»? Все её группы будут удалены, а игроки вернутся в резерв.`,
    stageTitle: (name: string) => `Этап: ${name}`,
    stageAria: (name: string) => `Этап ${name}`,
    stage: 'Этап',
  },
}
